<template>
  <div class="multi-load-select">
    <el-select
      ref="selectRef"
      v-model="innerValue"
      class="multi-select-el"
      :multiple="multiple"
      :filterable="filterable"
      :clearable="clearable"
      :placeholder="placeholder"
      :disabled="disabled"
      v-el-select-loadmore="handleLoadMore"
      @change="handleChange"
      @visible-change="handleVisibleChange"
      :filter-method="remote ? null : handleFilter"
      :remote-method="handleRemoteSearch"
      :remote="remote"
      :loading="loading"
      :value-key="optionValueLabel ? optionValueLabel.join('.') : ''"
    >
      <el-option
        v-for="item in renderList"
        :key="getCombineLabel(item)"
        :label="getCombineLabel(item)"
        :value="item"
      />
    </el-select>
  </div>
</template>

<script>
import Vue from 'vue'
import _ from 'lodash'

// 全局自定义下拉触底加载指令
Vue.directive('el-select-loadmore', {
  inserted (el, binding) {
    setTimeout(() => {
      const wrapDom = el.querySelector('.el-select-dropdown .el-select-dropdown__wrap')
      if (!wrapDom) return

      let scrollTimer = null
      const scrollFn = () => {
        clearTimeout(scrollTimer)
        scrollTimer = setTimeout(() => {
          const { scrollHeight, scrollTop, clientHeight } = wrapDom
          const isBottom = scrollHeight - scrollTop <= clientHeight + 30
          if (isBottom) {
            binding.value()
          }
        }, 100)
      }
      wrapDom.addEventListener('scroll', scrollFn)
      el._scrollHandler = scrollFn
    }, 0)
  },
  unbind (el) {
    const wrapDom = el.querySelector('.el-select-dropdown .el-select-dropdown__wrap')
    if (wrapDom && el._scrollHandler) {
      wrapDom.removeEventListener('scroll', el._scrollHandler)
      delete el._scrollHandler
    }
  }
})

export default {
  name: 'MultiLoadSelect',
  props: {
    value: {
      type: [Array, String, Number, Object],
      default () {
        return this.multiple ? [] : ''
      }
    },
    options: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: true
    },
    filterable: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputHeight: {
      type: Number,
      default: 200
    },
    initPageNum: {
      type: Number,
      default: 10
    },
    loadStep: {
      type: Number,
      default: 10
    },
    customFilter: {
      type: Function,
      default: null
    },
    remote: {
      type: Boolean,
      default: false
    },
    remoteDelay: {
      type: Number,
      default: 300
    },
    optionValueLabel: {
      type: Array,
      default: () => []
    },
  },
  data () {
    return {
      innerValue: this.multiple ? [] : '',
      currentPage: this.initPageNum,
      sourceList: [], // 原始完整数据源（永久不被过滤覆盖）
      filterList: [], // 过滤后的全量匹配列表
      renderList: [], // 分页切片后用于渲染的列表
      remoteTimer: null,
      loading: false,
      lastSearchQuery: '' // 缓存上次搜索关键词
    }
  },
  watch: {
    value: {
      handler (val) {
        if (this.multiple) {
          this.innerValue = Array.isArray(val) ? [...val] : []
        } else {
          this.innerValue = val
        }
      },
      immediate: true,
      deep: true
    },
    // 父组件传入完整options，存入原始数据源
    options: {
      handler (val) {
        this.sourceList = _.cloneDeep(val || [])
        // 数据源已整体更新（例如远程搜索返回新结果），过滤结果必须同步重置，
        // 否则 renderList 会停留在上一次的结果上，表现为“搜索没反应”
        this.filterList = [...this.sourceList]
        this.resetPage()
      },
      immediate: true,
      deep: true
    },
    multiple () {
      this.innerValue = this.multiple ? [] : ''
      this.resetPage()
    },
    // 监听分页/过滤列表，自动切片渲染
    filterList() {
      this.updateRenderList()
    },
    currentPage() {
      this.updateRenderList()
    }
  },
  methods: {
    // 下拉打开/关闭触发
    handleVisibleChange(visible) {
      if (!visible) {
        // 下拉关闭时：清空搜索缓存、恢复全量数据、清除组件内部query
        this.lastSearchQuery = ''
        // 重置过滤列表为完整原始数据
        this.filterList = [...this.sourceList]
        this.resetPage()
        // 清空el-select内部搜索输入框query，解决隐形关键词残留
        this.$nextTick(() => {
          if (this.$refs.selectRef) {
            this.$refs.selectRef.query = ''
          }
        })
      }
    },
    // 根据filterList和currentPage生成渲染列表
    updateRenderList() {
      this.renderList = this.filterList.slice(0, this.currentPage)
    },
    getCombineLabel (item) {
      if (!Array.isArray(this.optionValueLabel) || this.optionValueLabel.length === 0) {
        return item
      }
      if (typeof item !== 'object' || item === null) {
        return item
      }
      return this.optionValueLabel.reduce((obj, key) => {
        if (!obj) return ''
        return obj[key]
      }, item)
    },
    // 触底加载分页
    handleLoadMore () {
      // 判断过滤后的总匹配数据是否已经全部展示
      if (this.currentPage >= this.filterList.length) return
      this.currentPage += this.loadStep
    },
    // 本地前端过滤：基于原始sourceList过滤，不破坏完整数据
    handleFilter (query) {
      this.lastSearchQuery = query
      if (!query) {
        // 无搜索词，还原全部原始数据
        this.filterList = [...this.sourceList]
      } else {
        if (this.customFilter) {
          this.filterList = this.sourceList.filter(item => this.customFilter(item, query))
        } else {
          this.filterList = this.sourceList.filter(item => {
            const label = this.getCombineLabel(item)
            return String(label).includes(query)
          })
        }
      }
      // 每次搜索重置分页
      this.resetPage()
    },
    // 远程搜索防抖
    handleRemoteSearch (query) {
      clearTimeout(this.remoteTimer)
      this.loading = true
      this.remoteTimer = setTimeout(async () => {
        // 把 emit 包装成 Promise，resolve 就是父组件的回调
        await new Promise(resolve => {
          this.$emit('remote-search', query, resolve)
        })
        this.resetPage()
        this.loading = false
      }, this.remoteDelay)
    },
    handleBlur () {
      clearTimeout(this.remoteTimer)
      this.$emit('blur')
    },
    handleFocus () {
      this.$emit('focus')
    },
    handleChange (val) {
      // 这里不能置 loading=true：选中后没有复位时机，会让下拉一直显示“加载中”
      this.$emit('input', val)
      this.$emit('change', val)
    },
    // 重置分页并刷新渲染列表
    resetPage () {
      this.currentPage = this.initPageNum
      // 初始化过滤列表为全部原始数据
      if (!this.filterList.length) {
        this.filterList = [...this.sourceList]
      }
      this.updateRenderList()
    }
  }
}
</script>

<style scoped>
.multi-load-select {
  width: 100%;
}

.multi-select-el {
  width: 100%;
}

.multi-select-el /deep/ .el-input,
.multi-select-el /deep/ .el-input__inner {
  height: v-bind(inputHeight) !important;
}

.multi-select-el /deep/ .el-select__tags {
  max-height: v-bind(inputHeight) !important;
  overflow-y: auto;
}
</style>
